import { Bell } from 'lucide-react';
import CircularProgress from '../../common/CircularProgress';
import { PwaSwitch } from '../../../pwa/PwaSwitch';
import { usePushNotifications, type PushUiState } from '../../../pwa/usePushNotifications';

/** Texto bajo el título, según el estado de las push en ESTE navegador. */
const STATUS_TEXT: Record<PushUiState, string> = {
  loading: 'Cargando…',
  working: 'Un momento…',
  on: 'Activadas en este navegador: te avisamos de lo que pasa en Psi Mammoliti aunque no tengas la plataforma abierta.',
  off: 'Activalas para enterarte al instante de tus avisos de Psi Mammoliti en este navegador.',
  denied:
    'Bloqueaste las notificaciones de este sitio. Habilitalas desde el candado junto a la dirección del navegador (Notificaciones → Permitir) y volvé a cargar la página.',
  unsupported: 'Este navegador no permite recibir notificaciones.',
  impersonated: 'Estás viendo la cuenta de otra persona: desde acá no se pueden activar las notificaciones.',
  // No se muestra (ver abajo): el estado existe por completitud del tipo.
  unconfigured: '',
};

/** El interruptor solo se puede mover cuando hay algo para prender o apagar. */
const INTERACTIVE_STATES: PushUiState[] = ['on', 'off'];

/**
 * Tarjeta de Perfil → Información general (solo web) para activar o desactivar las notificaciones push en ESTE navegador, igual
 * que se hace en la app mobile (mismo hook y mismo registro en el backend: `usePushNotifications`). El navegador queda vinculado
 * a la cuenta que las activa: el backend asocia el dispositivo a la psicóloga de la sesión y, si otra cuenta las activa después
 * en el mismo navegador, se lo reasigna — nunca recibe las de dos cuentas a la vez.
 *
 * No se muestra mientras las push no estén configuradas en este ambiente (desarrollo local) — mantiene el Perfil sin ruido.
 */
export function WebPushNotificationsCard() {
  const { state, error, enable, disable } = usePushNotifications();
  const busy = state === 'loading' || state === 'working';

  if (state === 'unconfigured') return null;

  return (
    <div
      data-testid="web-push-card"
      className="bg-gray-50 rounded-xl px-3 pt-2.5 pb-3 sm:px-4 sm:pt-3 sm:pb-4 min-w-0 mb-4 sm:mb-5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex items-start gap-2">
          <Bell size={18} className="text-gray-500 mt-0.5 shrink-0" aria-hidden />
          <div className="min-w-0">
            <p className="text-[10px] sm:text-[11px] font-medium uppercase tracking-wider mb-1 sm:mb-1.5 text-gray-400">
              Notificaciones
            </p>
            <p className="text-xs sm:text-sm text-gray-700 break-words" data-testid="web-push-status">
              {STATUS_TEXT[state]}
            </p>
            {(state === 'off' || state === 'on') && (
              <p className="text-[11px] sm:text-xs text-gray-400 break-words mt-1">
                Este navegador queda vinculado a tu cuenta. Si otra persona las activa en este mismo navegador, dejás de recibirlas acá.
              </p>
            )}
          </div>
        </div>

        {busy ? (
          <CircularProgress sizeRem={1.5} label={state === 'loading' ? 'Cargando' : 'Un momento'} />
        ) : (
          <PwaSwitch
            label="Recibir notificaciones en este navegador"
            checked={state === 'on'}
            disabled={!INTERACTIVE_STATES.includes(state)}
            onChange={(next) => (next ? enable() : disable())}
          />
        )}
      </div>

      {error && (
        <p role="alert" className="text-xs text-coral mt-2">
          {error}
        </p>
      )}
    </div>
  );
}
